import { CSSProperties } from 'react';

import { cellKey, parseMergeRanges, SheetCell, SheetGrid } from './sheet-grid.util';
import { formatCellNumber } from './sheet-number-format.util';
import {
  borderWidthPx,
  columnWidthPx,
  cssColour,
  horizontalAlign,
  rowHeightPx,
  verticalAlign,
} from './sheet-style.util';
import { ExcelTableModel, tableRegionsOf, tableStyleAt } from './table-style.util';

/**
 * The shape of ExcelJS this reader uses, named here rather than imported.
 *
 * The library's own types come from its Node entry, while the page loads its browser bundle; and
 * a reader that states the handful of fields it touches can be tested with a plain object instead
 * of a real workbook.
 */
type ExcelColour = { argb?: string };

type ExcelCell = {
  alignment?: { horizontal?: string; vertical?: string; wrapText?: boolean };
  border?: Record<string, { color?: ExcelColour; style?: string } | undefined>;
  fill?: { fgColor?: ExcelColour; pattern?: string; type?: string };
  font?: { bold?: boolean; color?: ExcelColour; italic?: boolean; size?: number };
  numFmt?: string;
  text?: string;
  value?: unknown;
};

type ExcelRow = {
  eachCell: (
    options: { includeEmpty: boolean },
    each: (cell: ExcelCell, column: number) => void,
  ) => void;
  height?: number;
};

export type ExcelWorksheet = {
  columnCount: number;
  columns?: { width?: number }[];
  eachRow: (
    options: { includeEmpty: boolean },
    each: (row: ExcelRow, index: number) => void,
  ) => void;
  /** Each entry carries its model under `table`; older shapes are the model itself. */
  getTables?: () => ({ table?: ExcelTableModel } & ExcelTableModel)[];
  model?: { merges?: string[] };
  name: string;
  views?: { showGridLines?: boolean }[];
};

/**
 * The faint rule Excel draws between cells unless the sheet turns it off.
 *
 * Without it a spreadsheet reads as a column of loose text: the grid is how a reader tells which
 * value sits in which column, and it is the one piece of a worksheet's look that is not stored
 * anywhere in the cells. A cell's own border paints over it.
 */
const GRID_LINE: CSSProperties = { border: '1px solid #E4E7EC' };

/** Keys the file did not set must not overwrite what a table or the grid put underneath. */
const defined = (style: CSSProperties): CSSProperties =>
  Object.fromEntries(Object.entries(style).filter(([, value]) => value !== undefined));

const BORDER_SIDES = ['top', 'right', 'bottom', 'left'] as const;

const borderStyle = (cell: ExcelCell): CSSProperties => {
  const style: Record<string, string> = {};

  for (const side of BORDER_SIDES) {
    const border = cell.border?.[side];

    if (!border?.style) continue;

    const colour = cssColour(border.color?.argb) ?? '#D0D5DD';

    style[`border${side[0].toUpperCase()}${side.slice(1)}`] =
      `${borderWidthPx(border.style)}px solid ${colour}`;
  }

  return style;
};

const cellText = (cell: ExcelCell): string => {
  if (typeof cell.value === 'number') {
    const formatted = formatCellNumber(cell.value, cell.numFmt);

    if (formatted !== null) return formatted;
  }

  return cell.text ?? '';
};

const cellStyle = (cell: ExcelCell): CSSProperties => {
  // Only a solid fill is a background; Excel's patterns and gradients have no honest CSS here.
  const background =
    cell.fill?.type === 'pattern' && cell.fill.pattern === 'solid'
      ? cssColour(cell.fill.fgColor?.argb)
      : undefined;

  return {
    backgroundColor: background,
    color: cssColour(cell.font?.color?.argb),
    fontSize: cell.font?.size ? `${cell.font.size}pt` : undefined,
    fontStyle: cell.font?.italic ? 'italic' : undefined,
    fontWeight: cell.font?.bold ? 600 : undefined,
    textAlign: horizontalAlign(
      cell.alignment?.horizontal,
      typeof cell.value === 'number',
    ) as CSSProperties['textAlign'],
    verticalAlign: verticalAlign(cell.alignment?.vertical) as CSSProperties['verticalAlign'],
    whiteSpace: cell.alignment?.wrapText ? 'pre-wrap' : 'pre',
    ...borderStyle(cell),
  };
};

/**
 * One worksheet as the table draws it — values under their own number format, the file's colours,
 * fonts, borders, column widths and merged blocks.
 *
 * All of it comes out of the workbook rather than out of a theme of ours, which is what makes an
 * exported cap table look in the viewer the way it looks in Excel: this product's exports set
 * every colour explicitly, so there is nothing left to guess.
 */
export const worksheetToGrid = (sheet: ExcelWorksheet): SheetGrid => {
  const merges = parseMergeRanges(sheet.model?.merges ?? []);
  const tables = tableRegionsOf(
    (sheet.getTables?.() ?? []).map((entry) => entry.table ?? (entry as ExcelTableModel)),
  );
  const showGridLines = sheet.views?.[0]?.showGridLines !== false;
  const rows: SheetGrid['rows'] = [];

  sheet.eachRow({ includeEmpty: true }, (row, rowIndex) => {
    const byColumn = new Map<number, ExcelCell>();

    row.eachCell({ includeEmpty: true }, (cell, columnIndex) => byColumn.set(columnIndex, cell));

    const cells: SheetCell[] = [];

    // Every row to the sheet's full width rather than to its own last value: a worksheet is a
    // rectangle, and a row that stopped early would leave a hole in the grid where Excel draws
    // an empty cell — most visibly on a row that holds nothing at all.
    for (let columnIndex = 1; columnIndex <= sheet.columnCount; columnIndex += 1) {
      // A cell a merged block swallowed is not drawn: the block's first cell spans over it.
      if (merges.covered.has(cellKey(rowIndex, columnIndex))) continue;

      const cell = byColumn.get(columnIndex);
      const span = merges.spans.get(cellKey(rowIndex, columnIndex));

      cells.push({
        colSpan: span?.colSpan ?? 1,
        rowSpan: span?.rowSpan ?? 1,
        // Underneath what the file says about this cell: the sheet's grid, then whatever table
        // reaches it. Anything the cell sets itself paints over both.
        style: {
          ...(showGridLines ? GRID_LINE : {}),
          ...tableStyleAt(tables, rowIndex, columnIndex),
          ...(cell ? defined(cellStyle(cell)) : {}),
        },
        text: cell ? cellText(cell) : '',
      });
    }

    rows.push({ cells, height: rowHeightPx(row.height) });
  });

  return {
    columns: Array.from({ length: sheet.columnCount }, (_, index) =>
      columnWidthPx(sheet.columns?.[index]?.width),
    ),
    name: sheet.name,
    rows,
  };
};
